/**
 * Formats the postcode input value.
 *
 * @param {string} previousValue - The previous postcode value.
 * @param {string} inputValue - The new postcode input value.
 * @return {string} The formatted postcode value.
 */
/**
 * Formats the postcode input value.
 *
 * @param {string} previousValue - The previous postcode value.
 * @param {string} inputValue - The new postcode input value.
 * @return {string} The formatted postcode value.
 */

export const desktopBreakpoint = 900;

export const formatPostcodeValue = (previousValue, inputValue) => {
  // Convert the input value to uppercase and remove any non-alphanumeric characters.
  const formattedValue = inputValue.toUpperCase().replace(/[^A-Z0-9]/g, "");

  // If the value has a length less than or equal to 8,
  // format the value by inserting a space after the fourth character.
  if (formattedValue.length <= 8) {
    const insertSpaceAt = formattedValue.length - 3;
    // Insert a space after the fourth character.
    const formattedPostcode = `${formattedValue.slice(
      0,
      insertSpaceAt
    )} ${formattedValue.slice(insertSpaceAt)}`;
    return formattedPostcode;
  }

  // Return the previous postcode value if the value is not formatted.
  return previousValue;
};

export const capitalizeFirstLetters = (fullName) => {
  return fullName
    ?.split?.(' ')
    .map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(' ');
  }

export const formatUserDataToSave = (user) => {
  return {
    givenName: user?.displayName?.split(' ')?.[0] || '',
    surname: user?.displayName?.split(' ')?.[1] || '',
    phone: user?.providerData?.[0]?.phoneNumber || '',
    photoURL: user?.providerData?.[0]?.photoURL || '',
    email: user?.email || '',
    accessToken: user?.stsTokenManager?.accessToken || '',
    refreshToken: user?.stsTokenManager?.refreshToken || '',
    expirationTime: user?.stsTokenManager?.expirationTime || '',
    loginPlatform: "google",
    isLoggedIn: true
  }
} 

export const mobileTodesktopVWConverter = (mobileValue) => {
  const pixelValue = (mobileValue/100)*1080;
  return (pixelValue/2500)*100;
}

export const pxTovwConverter = (pxValue,type = 'mobile') => {
  let screenSize = 1080;
  if(type === 'desktop'){
    screenSize = 2500;
  }
  return (pxValue/screenSize)*100;
}

export const pxTovwConverter375 = (pxValue,type = 'mobile') => {
  let screenSize = 375;
  if(type === 'desktop'){
    screenSize = 868.05;
  }
  return (pxValue/screenSize)*100;
}

export const pxTovwConverter1440 = (pxValue) => {
  let screenSize = 1440;
  return (pxValue/screenSize)*100;
}

export const validateSignature = (signatureRef) => {
  if (!signatureRef.current) return "Canvas not initialized";

  const canvas = signatureRef.current.getCanvas(); // Get the canvas element
  const ctx = canvas.getContext("2d");
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

  const isValid = analyzeSignature(imageData);

  return isValid;
};

const analyzeSignature = (imageData) => {
  const { width, height, data } = imageData;

  let pixelCount = 0;
  let minX = width, maxX = 0, minY = height, maxY = 0;
  const path = [];
  let pathLength = 0;

  // Collect all non-transparent pixels and their coordinates
  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3]; // Check the alpha channel for non-transparent pixels
    if (alpha > 0) {
      const x = (i / 4) % width;
      const y = Math.floor((i / 4) / width);

      pixelCount++;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);

      path.push({ x, y });
    }
  }

  const widthCoverage = maxX - minX;
  const heightCoverage = maxY - minY;

  // Step 1: Validate bounding box dimensions (Relaxed)
  if (widthCoverage < 30 || heightCoverage < 10) { 
    return false; // Too small
  }

  // Step 2: Validate pixel count (Relaxed)
  if (pixelCount < 300) {
    return false; // Not enough detail
  }

  // Step 3: Compute directional changes and path length
  let directionChanges = 0;
  let crossDetected = false;
  let intersectionCount = 0; // Track number of intersections
  pathLength = 0;
  
  // To track if a cross-like pattern appears, we will need two linear paths.
  let linePaths = [];

  for (let i = 1; i < path.length - 1; i++) {
    const prev = path[i - 1];
    const current = path[i];
    const next = path[i + 1];

    // Compute angles between consecutive points
    const angle1 = Math.atan2(current.y - prev.y, current.x - prev.x);
    const angle2 = Math.atan2(next.y - current.y, next.x - current.x);

    const angleDifference = Math.abs(angle2 - angle1);
    if (angleDifference > Math.PI / 4) { // Relaxed angle difference threshold
      directionChanges++;
    }

    // Estimate path length
    const dx = current.x - prev.x;
    const dy = current.y - prev.y;
    pathLength += Math.sqrt(dx * dx + dy * dy);

    // Detect cross pattern: Two main lines intersecting
    if (linePaths.length < 2) {
      if (linePaths.length === 0) {
        // First line
        linePaths.push({ start: prev, end: current });
      } else {
        // Second line, check for intersection
        const line1 = linePaths[0];
        if (checkIntersection(line1.start, line1.end, prev, current)) {
          intersectionCount++; // Increment intersection count
          // Only consider it a cross if there are multiple intersections
          if (intersectionCount >= 2) { 
            crossDetected = true;
            break;
          }
        }
        // If no intersection found, add current line as second candidate
        linePaths.push({ start: prev, end: current });
      }
    }
  }

  // If a cross is detected, return false for invalid signature
  if (crossDetected) {
    return false; // Invalid signature due to cross
  }

  // Step 4: Enforce minimum path length relative to bounding box (Relaxed)
  const boundingBoxDiagonal = Math.sqrt(widthCoverage ** 2 + heightCoverage ** 2);
  if (pathLength < boundingBoxDiagonal * 1.5) { 
    return false; // Path is too short
  }

  // Step 5: Check aspect ratio to avoid overly linear signatures (Relaxed)
  const aspectRatio = widthCoverage / heightCoverage;
  if (aspectRatio > 15 || aspectRatio < 0.05) { 
    return false; // Too linear
  }

  // Step 6: Validate pixel density (Relaxed)
  const density = pixelCount / (widthCoverage * heightCoverage);
  if (density < 0.03) { 
    return false; // Pixels are too sparse
  }

  return true;
};

// Helper function to check if two lines intersect
const checkIntersection = (line1Start, line1End, line2Start, line2End) => {
  const denom = (line1End.x - line1Start.x) * (line2End.y - line2Start.y) - (line1End.y - line1Start.y) * (line2End.x - line2Start.x);
  if (denom === 0) return false; // Parallel lines, no intersection

  const t1 = ((line2Start.x - line1Start.x) * (line2End.y - line2Start.y) - (line2Start.y - line1Start.y) * (line2End.x - line2Start.x)) / denom;
  const t2 = ((line2Start.x - line1Start.x) * (line1End.y - line1Start.y) - (line2Start.y - line1Start.y) * (line1End.x - line1Start.x)) / denom;

  return t1 >= 0 && t1 <= 1 && t2 >= 0 && t2 <= 1; // Lines intersect if t1 and t2 are between 0 and 1
};